/**
 * Assistant catalog + access requests.
 *
 * The catalog is every released assistant with whether the caller may use
 * it. It is deliberately a separate call from `listVisibleAssistants`: that
 * list feeds the ask bar, sample questions and the in-chat picker, which
 * assume every entry is usable. Only the Hub's locked tiles read this.
 *
 * Its own module rather than more of `admin.ts` / `conversations.ts`, so the
 * feature stays in one place.
 */
import { acquireToken, USE_REAL_AUTH } from "../auth/msal";
import { noteRequestId } from "../error-reporting";

const API_BASE = import.meta.env.VITE_API_BASE ?? "";

async function authHeaders(devUser?: string): Promise<Record<string, string>> {
  if (USE_REAL_AUTH) {
    const t = await acquireToken();
    return t ? { Authorization: `Bearer ${t.token}` } : {};
  }
  return devUser ? { "X-Dev-User": devUser } : {};
}

export class AccessRequestError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

async function check(r: Response): Promise<Response> {
  noteRequestId(r.headers.get("X-Request-Id"));
  if (r.ok) return r;
  let detail = "";
  try {
    const body = await r.text();
    try {
      const parsed = JSON.parse(body);
      detail =
        typeof parsed?.detail === "string" ? parsed.detail : body.slice(0, 300);
    } catch {
      detail = body.slice(0, 300);
    }
  } catch {
    detail = r.statusText;
  }
  if (r.status === 403 && !detail) detail = "Forbidden";
  throw new AccessRequestError(detail || `HTTP ${r.status}`, r.status);
}

export type AccessRequestStatus = "open" | "granted" | "rejected" | "handled";

export interface CatalogRequest {
  id: number;
  status: AccessRequestStatus;
  created_at: string | null;
  handled_at: string | null;
}

/** One catalog row. A blocked row carries nothing beyond what is shown on
 * the tile — no settings, no sample questions. */
export interface CatalogAssistant {
  slug: string;
  name: string;
  description: string;
  icon: string | null;
  maturity: "production" | "pilot";
  accessible: boolean;
  /** One plain sentence, set only when `accessible` is false. */
  blocked_reason: string | null;
  request_status: AccessRequestStatus | null;
  request: CatalogRequest | null;
}

export async function getCatalog(
  devUser?: string,
): Promise<CatalogAssistant[]> {
  const r = await fetch(`${API_BASE}/api/assistants/catalog`, {
    headers: await authHeaders(devUser),
    cache: "no-store",
  });
  // The Hub works without the catalog (it just shows no locked tiles), so a
  // failure here is not worth an error on the landing page.
  if (!r.ok) return [];
  return (await r.json()) as CatalogAssistant[];
}

export interface AccessRequestRow {
  id: number;
  user_oid: string;
  user_email: string;
  user_name: string;
  assistant_slug: string;
  reason: string;
  status: AccessRequestStatus;
  created_at: string | null;
  handled_at: string | null;
  handled_by_oid: string;
  handler_note: string;
}

export async function requestAccess(
  slug: string,
  reason: string,
  devUser?: string,
): Promise<{ request: AccessRequestRow; created: boolean }> {
  const r = await check(
    await fetch(
      `${API_BASE}/api/assistants/${encodeURIComponent(slug)}/access-requests`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(await authHeaders(devUser)),
        },
        body: JSON.stringify({ reason }),
      },
    ),
  );
  return (await r.json()) as { request: AccessRequestRow; created: boolean };
}

// ── admin ──────────────────────────────────────────────────────────────────

export interface AdminAccessRequestRow extends AccessRequestRow {
  assistant_name: string;
  /** Re-evaluated live: may this person open it now? null = unknown. */
  has_access: boolean | null;
}

export async function listAccessRequests(
  which: "open" | "all",
  devUser?: string,
): Promise<AdminAccessRequestRow[]> {
  const r = await check(
    await fetch(`${API_BASE}/api/admin/access-requests?status=${which}`, {
      headers: await authHeaders(devUser),
      cache: "no-store",
    }),
  );
  const body = (await r.json()) as { requests?: AdminAccessRequestRow[] };
  return body.requests ?? [];
}

export interface AccessRequestCounts {
  open: number;
  granted: number;
  rejected: number;
  handled: number;
  total: number;
}

export interface AccessRequestSummary {
  since: string;
  days: number;
  open_now: number;
  totals: AccessRequestCounts;
  by_assistant: (AccessRequestCounts & {
    assistant_slug: string;
    assistant_name: string;
  })[];
}

export async function getAccessRequestSummary(
  devUser?: string,
): Promise<AccessRequestSummary> {
  const r = await check(
    await fetch(`${API_BASE}/api/admin/access-requests/summary`, {
      headers: await authHeaders(devUser),
      cache: "no-store",
    }),
  );
  return (await r.json()) as AccessRequestSummary;
}

export async function handleAccessRequest(
  id: number,
  status: Exclude<AccessRequestStatus, "open">,
  note: string,
  devUser?: string,
): Promise<AccessRequestRow> {
  const r = await check(
    await fetch(`${API_BASE}/api/admin/access-requests/${id}/handle`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(await authHeaders(devUser)),
      },
      body: JSON.stringify({ status, note }),
    }),
  );
  return ((await r.json()) as { request: AccessRequestRow }).request;
}

/** "7 Oct" — short enough for a tile. */
export function shortDate(ts: string | null | undefined): string {
  if (!ts) return "";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}
